import { DownloadBlob } from '@browser/Browser';
import { isNullOrWhitespace, roundDecimal, strCompareLowerCase } from "@common/Common";
import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue';
import { IEvaluationCommand } from '@Evaluation/cqrs';
import { EmailSenderFormSubmitEventPayload, EmailSenderViewModel } from '@Evaluation/Estimations/Components/EmailSender';
import EmailSender from '@Evaluation/Estimations/Components/EmailSender.vue';
import PanelistAlternativesSelector from '@Evaluation/Estimations/Components/PanelistAlternativesSelector.vue';
import PanelistsSelector from '@Evaluation/Estimations/Components/PanelistsSelector.vue';
import { SurveyAccessLinksViewModel } from '@Evaluation/Estimations/Components/SurveyAccessLinks';
import SurveyAccessLinks from '@Evaluation/Estimations/Components/SurveyAccessLinks.vue';
import { ExcludePanelistCommandV1, IncludePanelistCommandV1, SendEmailCommandV1, UpdatePanelistAlternativesCommandV1 } from "@Evaluation/Estimations/Domain/commands-core";
import EstimationsCommands from "@Evaluation/Estimations/Domain/commands-vue-component";
import EstimationsQueries from "@Evaluation/Estimations/Domain/queries-vue-component";
import { AlternativeReadModel, DownloadReadModel, PanelistReadModel, PanelistsPageReadModel } from '@Evaluation/Estimations/Domain/readmodels';
import Component, { mixins } from 'vue-class-component';


@Component({ components: { PortfolioNavigation, PanelistAlternativesSelector, PanelistsSelector, EmailSender, SurveyAccessLinks } })
export default class PanelistsPage extends mixins(EstimationsCommands, EstimationsQueries) {

    get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
    get projectId(): number { return Number.parseInt(this.$route.params.projectId); }
    get surveyId(): number { return Number.parseInt(this.$route.params.surveyId); }

    Initialized: boolean = false;

    tags: string[] = null;
    panelists: PanelistReadModel[] = null;

    SurveyName: string;
    TargetingEnabled: boolean = null;
    TargetingLabel: string;
    EnabledAddingPanelists: boolean = null;
    SearchPhrase: string = null;
    tagFilter: string = null;
    progressFilter: string = null;
    alternatives: AlternativeReadModel[] = null;
    surveyAccessLinks: SurveyAccessLinksViewModel = new SurveyAccessLinksViewModel();
    emailSender: EmailSenderViewModel = new EmailSenderViewModel();

    panelistAlternativesSelector: PanelistAlternativesSelectorProps = null;
    panelistsSelector: PanelistsSelectorProps = null;

    get Progress(): number 
    {
        const questionsCount = this.panelists.map(a => a.QuestionsCount)
            .reduce((questionsCount, curr) => questionsCount + curr, 0);

        if (questionsCount === 0)
        {
            return 0;
        }

        const answersCount = this.panelists.map(a => a.AnswersCount)
            .reduce((answersCount, curr) => answersCount + curr, 0);

        const progress = roundDecimal(100 * answersCount / questionsCount, 0);
        return progress;
    }

    get PanelistsView(): PanelistReadModel[]
    {
        let view = this.panelists.filter(a => a.IsIncluded);

        if (!isNullOrWhitespace(this.SearchPhrase))
        {
            const phrase = this.SearchPhrase.toLowerCase();
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) >= 0);
        }

        if (!isNullOrWhitespace(this.progressFilter))
        {
            if (this.progressFilter === 'Not started')
            {
                view = view.filter(a => a.Progress === 0);
            }
            else if (this.progressFilter === 'In progress')
            {
                view = view.filter(a => a.Progress > 0 && a.Progress < 100);
            }
            else if (this.progressFilter === 'Completed')
            {
                view = view.filter(a => a.Progress === 100);
            }
        }

        if (this.tagFilter)
        {
            view = view.filter(a => a.Tags.indexOf(this.tagFilter) > -1);
        }

        view = view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));

        return view;
    }

    get NonePanelistsSelected(): boolean 
    {
        return this.panelists.some(a => a.IsSelected) === false;
    }

    get AnyPanelistsSelected(): boolean 
    {
        return this.panelists.some(a => a.IsSelected);
    }

    get AllPanelistsSelected(): boolean 
    {
        return this.panelists.length > 0 && this.panelists.some(a => a.IsSelected === false) === false;
    }

    get SelectedPanelistsCount(): number 
    {
        return this.panelists.filter(a => a.IsSelected === true).length;
    }

    get FilterLabel(): string 
    {
        if (this.tagFilter)
        {
            return this.tagFilter;
        }

        if (this.progressFilter)
        {
            return this.progressFilter;
        }

        return "All panelists";
    }

    get panelistsIds(): number[]
    {
        return this.panelists.map(a => a.ProjectUserId);
    }


    getProgressStr(progress: number): string
    {
        return progress != null ? `${progress}%` : null;
    }


    filterByProgress(filter: string)
    {
        this.tagFilter = null;
        this.progressFilter = filter;
    }

    filterByTag(t: string)
    {
        this.progressFilter = null;
        this.tagFilter = t;
    }

    resetFilters()
    {
        this.progressFilter = null;
        this.tagFilter = null;
    }


    EmailOneCommand(projectUserId: number)
    {
        const p = this.panelists.find(a => a.ProjectUserId === projectUserId);

        if (p.HasEmail)
        {
            this.emailSender.show([p.ProjectUserId]);
        }
        else
        {
            alert("Email not provided");
        }
    }

    EmailManyCommand()
    {
        this.emailSender.show(this.panelists.filter(a => a.IsSelected && a.HasEmail).map(a => a.ProjectUserId));
    }

    EmailAllCommand()
    {
        this.emailSender.show(this.panelists.filter(a => a.IsIncluded).map(a => a.ProjectUserId));
    }


    showAccessLinks()
    {
        this.surveyAccessLinks.show(this.surveyId);
        this.surveyAccessLinks.setData(this.panelists.filter(a => a.IsIncluded));
    }

    downloadLinks()
    {
        this.runExportAccessLinksBySurveyIdQueryV1(this.workspaceId, this.surveyId);
    }


    SelectAllCommand()
    {
        this.PanelistsView.forEach(a => a.IsSelected = true);
    }

    UnselectAllCommand()
    {
        this.PanelistsView.forEach(a => a.IsSelected = false);
    }

    InverseSelectionCommand()
    {
        this.PanelistsView.forEach(a => a.IsSelected = !a.IsSelected);
    }


    showPanelistsSelector()
    {
        this.panelistsSelector = new PanelistsSelectorProps(this.workspaceId, this.projectId, this.TargetingEnabled, this.tags, this.panelists);
    }

    closePanelistsSelector()
    {
        this.panelistsSelector = null;
    }


    showPanelistAlternativesSelector(projectUserId: number)
    {
        this.panelistsSelector?.hide();

        const p = this.panelists.find(a => a.ProjectUserId === projectUserId);
        this.panelistAlternativesSelector = new PanelistAlternativesSelectorProps(p.ProjectUserId.toString(), p.Name, this.alternatives, p.AlternativesIds);
    }

    closePanelistAlternativesSelector()
    {
        this.panelistsSelector?.show();

        this.panelistAlternativesSelector = null;
    }


    handleUpdatePanelistAlternativesCommand(e: any)
    {
        if (this.TargetingEnabled)
        {
            const projectUserId = Number.parseInt(e.CorrelationId);
            this.invokeUpdatePanelistAlternativesCommandV1(this.workspaceId, this.surveyId, projectUserId, e.SelectedIds);
        }
    }


    handleIncludePanelistCommand(projectUserId: number)
    {
        if (!this.TargetingEnabled)
        {
            const p = this.panelists.find(a => a.ProjectUserId === projectUserId);
            this.invokeIncludePanelistCommandV1(this.workspaceId, this.surveyId, p.ProjectUserId);
        }
    }

    handleExcludePanelistCommand(projectUserId: number)
    {
        if (!this.TargetingEnabled)
        {
            const p = this.panelists.find(a => a.ProjectUserId === projectUserId);
            this.invokeExcludePanelistCommandV1(this.workspaceId, this.surveyId, p.ProjectUserId);
        }
    }

    handleSendEmailCommand(e: EmailSenderFormSubmitEventPayload)
    {
        this.invokeSendEmailCommandV1(this.workspaceId, this.surveyId, e.recipientIds, e.emailSubject, e.emailBody);
    }


    mounted()
    {
        document.title = "Panelists | TransparentChoice";
        this.runPanelistsPageQueryV1(this.surveyId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName == "PanelistsPageReadModel")
        {
            const rm = model as PanelistsPageReadModel;

            this.tags = rm.ProjectUsersTags;
            this.SurveyName = rm.SurveyName;
            this.TargetingEnabled = rm.TargetingAttributeId != null;
            this.TargetingLabel = this.TargetingEnabled ? rm.TargetingAttributeName : "Disabled";
            this.EnabledAddingPanelists = rm.EnabledAddingPanelists;
            this.alternatives = rm.Alternatives;
            this.panelists = rm.Panelists;

            this.Initialized = true;
        }
        else if (modelName == "PanelistReadModel")
        {
            const rm = model as PanelistReadModel;

            const curr = this.panelists.find(a => a.ProjectUserId === rm.ProjectUserId);

            if (curr)
            {
                this.panelists.remove(curr);
            }

            this.panelists.push(rm);
        }
        else if (modelName === "DownloadReadModel")
        {
            const rm = model as DownloadReadModel;
            DownloadBlob(rm.Url);
        }
    }

    onCommandSucceed(cmd: IEvaluationCommand, correlationId: string)
    {
        if (IncludePanelistCommandV1.matchType(cmd))
        {
            const projectUserId = Number.parseInt(correlationId);
            this.runPanelistQueryV1(this.workspaceId, this.surveyId, projectUserId);
        }
        else if (ExcludePanelistCommandV1.matchType(cmd))
        {
            const projectUserId = Number.parseInt(correlationId);
            this.panelists.find(a => a.ProjectUserId === projectUserId).IsIncluded = false;
        }
        else if (UpdatePanelistAlternativesCommandV1.matchType(cmd))
        {
            const projectUserId = Number.parseInt(correlationId);
            this.runPanelistQueryV1(this.workspaceId, this.surveyId, projectUserId);

            this.closePanelistAlternativesSelector();
        }
        else if (SendEmailCommandV1.matchType(cmd))
        {
            alert("Sent");
            this.emailSender.close();
        }

    }
}

class PanelistAlternativesSelectorProps
{
    CorrelationId: string;
    Title: string;
    Alternatives: AlternativeReadModel[];
    SelectedAlternativesIds: number[];

    constructor(correlationId: string, title: string, alternatives: AlternativeReadModel[], selectedAlternativesIds: number[])
    {
        this.CorrelationId = correlationId;
        this.Title = title;
        this.Alternatives = alternatives;
        this.SelectedAlternativesIds = selectedAlternativesIds;
    }
}

class PanelistsSelectorProps
{
    WorkspaceId: number = null;
    ProjectId: number = null;
    TargetingEnabled: boolean = null;
    Tags: string[] = null;
    Panelists: PanelistReadModel[] = null;
    Visible: boolean = null;

    constructor(workspaceId: number, projectId: number, targetingEnabled: boolean, tags: string[], panelists: PanelistReadModel[])
    {
        this.WorkspaceId = workspaceId;
        this.ProjectId = projectId;
        this.TargetingEnabled = targetingEnabled;
        this.Tags = tags;
        this.Panelists = panelists;
        this.Visible = true;
    }

    hide()
    {
        this.Visible = false;
    }

    show()
    {
        this.Visible = true;
    }
}

